import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiMail, FiPhone, FiMapPin, FiInstagram, FiFacebook, FiLock } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialLinks = [
    { 
      icon: FiInstagram, 
      href: "https://www.instagram.com/canvas.clarity/", 
      label: "Instagram" 
    },
    { 
      icon: FiFacebook, 
      href: "https://www.facebook.com/profile.php?id=61573053521437", 
      label: "Facebook" 
    },
    { 
      icon: FiMail, 
      href: "mailto:hello@canvas-clarity.com", 
      label: "Email" 
    }
  ];

  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-12">
          {/* Brand Section */}
          <div className="col-span-1 lg:col-span-2">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }} 
              className="mb-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiHeart} className="h-8 w-8 text-rose-400" />
                <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-purple-400 bg-clip-text text-transparent">
                  Canvas & Clarity
                </span>
              </div>
              <p className="text-gray-300 leading-relaxed max-w-md">
                Where art meets mindfulness and personal growth. We create transformative experiences at Canvas & Clarity that connect you with your true self.
              </p>
            </motion.div>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a 
                  key={index} 
                  href={social.href} 
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }} 
                  whileTap={{ scale: 0.95 }} 
                  className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-r hover:from-rose-500 hover:to-purple-600 rounded-lg flex items-center justify-center transition-all duration-300" 
                  aria-label={social.label}
                >
                  <SafeIcon icon={social.icon} className="h-5 w-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { name: "About Us", href: "#about" },
                { name: "Our Services", href: "#services" },
                { name: "Upcoming Events", href: "#events" },
                { name: "Gallery", href: "#gallery" },
                { name: "Contact", href: "#contact" }
              ].map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    onClick={(e) => handleNavClick(e, link.href)} 
                    className="text-gray-300 hover:text-rose-400 transition-colors duration-200 cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiMapPin} className="h-5 w-5 text-rose-400 mt-1 flex-shrink-0" />
                <div className="text-gray-300 text-sm">
                  550 Frontage Rd,<br />
                  Northfield, IL, 60093
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiPhone} className="h-5 w-5 text-rose-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">(312) 868-2598</span>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="h-5 w-5 text-rose-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">hello@canvas-clarity.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-gray-400 text-sm">
            © {currentYear} Canvas & Clarity Studio. All rights reserved.
          </div>
          <div className="flex items-center space-x-6 text-sm text-gray-400">
            <button 
              onClick={() => navigate('/login')} 
              className="flex items-center space-x-1 hover:text-rose-400 transition-colors group opacity-50 hover:opacity-100"
            >
              <SafeIcon icon={FiLock} className="w-3 h-3" />
              <span>Admin Portal</span>
            </button>
            <a href="#" className="hover:text-rose-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-rose-400 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;